/**
 * Admin → Payments (iter 84c)
 *
 * List of every `payment_transactions` row with refund controls.
 * Source: GET /api/admin/payment-transactions
 * Action: POST /api/admin/payment-transactions/{session_id}/refund
 */
import { useEffect, useMemo, useState } from "react";
import React from "react";
import { toast } from "sonner";
import {
  Activity,
  AlertTriangle,
  ChevronDown,
  ChevronRight,
  Loader2,
  RefreshCw,
  Repeat,
  RotateCcw,
} from "lucide-react";
import { api, formatApiError } from "@/lib/api";

function fmtCents(c) {
  return `$${((c || 0) / 100).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

function fmtDate(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString(undefined, {
      month: "short", day: "numeric", hour: "numeric", minute: "2-digit",
    });
  } catch {
    return "—";
  }
}

function StatusPill({ tx }) {
  if (tx.refunded) {
    return <span className="inline-flex items-center rounded-full border border-rose-500/40 bg-rose-500/15 text-rose-200 px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide">Refunded</span>;
  }
  if (tx.payment_status === "paid") {
    return <span className="inline-flex items-center rounded-full border border-emerald-500/40 bg-emerald-500/15 text-emerald-200 px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide">Paid</span>;
  }
  if (tx.status === "expired") {
    return <span className="inline-flex items-center rounded-full border border-amber-500/40 bg-amber-500/15 text-amber-200 px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide">Expired</span>;
  }
  return <span className="inline-flex items-center rounded-full border border-slate-700 bg-slate-800 text-slate-300 px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide">{tx.payment_status || "initiated"}</span>;
}

function RefundConfirm({ tx, onClose, onConfirmed }) {
  const [submitting, setSubmitting] = useState(false);
  const transferLegs = tx.transfer_legs || [];
  const transferredCount = transferLegs.filter((l) => l.status === "transferred").length;

  const submit = async () => {
    setSubmitting(true);
    try {
      const r = await api.post(`/admin/payment-transactions/${tx.session_id}/refund`);
      toast.success(`Refunded ${fmtCents(r.data.refund_amount_cents)}.`);
      onConfirmed(r.data);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center" onClick={onClose}>
      <div className="absolute inset-0 bg-black/60" />
      <div
        data-testid="refund-modal"
        onClick={(e) => e.stopPropagation()}
        className="relative w-[520px] rounded-2xl border border-slate-800 bg-slate-950 p-5"
      >
        <div className="flex items-center gap-2 mb-3">
          <AlertTriangle className="w-5 h-5 text-amber-400" />
          <div className="text-base font-bold text-slate-100">Confirm refund</div>
        </div>
        <p className="text-sm text-slate-400 mb-3">
          Refunding will (1) issue a Stripe refund for{" "}
          <span className="font-semibold text-slate-100">{fmtCents(tx.amount_cents)}</span> to the patient
          and (2) reverse <span className="font-semibold text-slate-100">{transferredCount}</span> transfer
          leg{transferredCount === 1 ? "" : "s"} to vendors/providers — pulling those funds back to the
          platform balance.
        </p>
        {transferLegs.length > 0 && (
          <ul className="rounded-lg border border-slate-800 bg-slate-900/40 p-3 mb-4 text-[11px] space-y-1.5">
            {transferLegs.map((leg, i) => (
              <li key={i} className="flex items-center justify-between gap-2">
                <span className="text-slate-300 truncate">
                  {leg.service_name} <span className="text-slate-500">({leg.route})</span>
                </span>
                <span className={`font-mono ${leg.status === "transferred" ? "text-rose-300" : "text-slate-500"}`}>
                  -{fmtCents(leg.amount_cents)}{" "}
                  <span className="text-[9px] uppercase">{leg.status}</span>
                </span>
              </li>
            ))}
          </ul>
        )}
        <div className="flex items-center justify-end gap-2">
          <button
            type="button"
            onClick={onClose}
            disabled={submitting}
            className="text-xs text-slate-400 hover:text-slate-200 px-3 py-1.5"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={submit}
            disabled={submitting}
            data-testid="refund-confirm-button"
            className="inline-flex items-center gap-1 rounded-full bg-rose-600 text-white text-xs font-semibold px-3 py-1.5 hover:bg-rose-700 disabled:opacity-50"
          >
            {submitting ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RotateCcw className="w-3.5 h-3.5" />}
            {submitting ? "Refunding…" : "Refund + reverse legs"}
          </button>
        </div>
      </div>
    </div>
  );
}

export default function AdminPayments() {
  const [txs, setTxs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [kindFilter, setKindFilter] = useState("all");
  const [expanded, setExpanded] = useState(null);
  const [refundFor, setRefundFor] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get("/admin/payment-transactions");
      setTxs(r.data?.transactions || []);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const filtered = useMemo(() => {
    if (kindFilter === "all") return txs;
    return txs.filter((t) => t.kind === kindFilter);
  }, [txs, kindFilter]);

  const onRefunded = (updated) => {
    setTxs((curr) =>
      curr.map((t) =>
        t.session_id === updated.session_id
          ? { ...t, refunded: true, refund_id: updated.refund_id, transfer_legs: updated.transfer_legs }
          : t
      )
    );
    setRefundFor(null);
  };

  return (
    <div className="min-h-screen bg-slate-950 text-slate-100">
      <div className="max-w-[1500px] mx-auto px-6 py-8">
        <div className="flex items-start justify-between mb-6">
          <div>
            <div className="text-[10px] uppercase tracking-[0.2em] font-bold text-slate-500">
              Admin · Payments
            </div>
            <h1 className="mt-1 text-3xl font-bold">Payment transactions</h1>
            <p className="mt-2 text-sm text-slate-400 max-w-3xl">
              Every flagship-program and pharmacy-direct checkout. Click a row to expand the
              settlement plan + transfer legs. Refund issues a Stripe refund AND reverses
              every transferred leg in one shot.
            </p>
          </div>
          <button
            type="button"
            onClick={load}
            disabled={loading}
            data-testid="payments-refresh"
            className="inline-flex items-center gap-1 rounded-full bg-slate-800 border border-slate-700 text-xs font-semibold px-3 py-1.5 text-slate-200 hover:bg-slate-700 disabled:opacity-50"
          >
            {loading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5" />}
            {loading ? "Loading…" : "Refresh"}
          </button>
        </div>

        <div className="flex items-center gap-2 mb-4">
          {[
            ["all", "All"],
            ["flagship_program", "Flagship"],
            ["pharmacy_direct", "Pharmacy"],
          ].map(([k, label]) => (
            <button
              key={k}
              type="button"
              onClick={() => setKindFilter(k)}
              data-testid={`payments-filter-${k}`}
              className={`rounded-full px-3 py-1.5 text-xs font-semibold border ${
                kindFilter === k
                  ? "bg-blue-600 text-white border-blue-500"
                  : "bg-slate-900 border-slate-800 text-slate-300 hover:bg-slate-800"
              }`}
            >
              {label}
            </button>
          ))}
        </div>

        {loading ? (
          <div className="text-sm text-slate-500 flex items-center gap-2">
            <Loader2 className="w-4 h-4 animate-spin" /> Loading…
          </div>
        ) : filtered.length === 0 ? (
          <div
            data-testid="payments-empty"
            className="rounded-2xl border border-dashed border-slate-800 bg-slate-900/30 p-12 text-center"
          >
            <Activity className="w-8 h-8 text-slate-600 mx-auto mb-3" />
            <div className="text-base font-semibold text-slate-200">No payments yet</div>
            <p className="mt-2 text-sm text-slate-400 max-w-md mx-auto">
              Once a patient checks out via <code className="text-slate-300">/buy/&lt;program-slug&gt;</code>, the
              transaction will appear here with its settlement plan + transfer legs.
            </p>
          </div>
        ) : (
          <div className="rounded-2xl border border-slate-800 bg-slate-900/40 overflow-hidden">
            <table className="w-full text-sm" data-testid="payments-table">
              <thead className="bg-slate-900/80 text-[10px] uppercase tracking-wider text-slate-500">
                <tr>
                  <th className="text-left px-4 py-3 w-8"></th>
                  <th className="text-left px-4 py-3">Patient</th>
                  <th className="text-left px-4 py-3">Kind</th>
                  <th className="text-right px-4 py-3">Amount</th>
                  <th className="text-left px-4 py-3">Status</th>
                  <th className="text-left px-4 py-3">Created</th>
                  <th className="text-right px-4 py-3">Action</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-800">
                {filtered.map((tx) => {
                  const isOpen = expanded === tx.session_id;
                  const canRefund = tx.payment_status === "paid" && !tx.refunded && tx.payment_intent_id;
                  return (
                    <React.Fragment key={tx.session_id}>
                      <tr
                        data-testid={`payment-row-${tx.session_id}`}
                        className="cursor-pointer hover:bg-slate-900/60"
                        onClick={() => setExpanded(isOpen ? null : tx.session_id)}
                      >
                        <td className="px-4 py-3 text-slate-500">
                          {isOpen ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
                        </td>
                        <td className="px-4 py-3">
                          <div className="font-semibold text-slate-100">{tx.patient_name || "—"}</div>
                          <div className="text-[11px] text-slate-500">{tx.patient_email || tx.session_id}</div>
                        </td>
                        <td className="px-4 py-3 text-slate-300">
                          <div className="flex items-center gap-1.5">
                            <span className="capitalize">{(tx.kind || "").replace("_", " ")}</span>
                            {tx.mode === "subscription" && (
                              <Repeat className="w-3 h-3 text-violet-300" />
                            )}
                          </div>
                        </td>
                        <td className="px-4 py-3 text-right font-mono text-slate-200">
                          {fmtCents(tx.amount_cents)}
                        </td>
                        <td className="px-4 py-3"><StatusPill tx={tx} /></td>
                        <td className="px-4 py-3 text-[11px] text-slate-500">{fmtDate(tx.created_at)}</td>
                        <td className="px-4 py-3 text-right">
                          {canRefund && (
                            <button
                              type="button"
                              onClick={(e) => {
                                e.stopPropagation();
                                setRefundFor(tx);
                              }}
                              data-testid={`payment-refund-${tx.session_id}`}
                              className="inline-flex items-center gap-1 rounded-full bg-rose-600/15 border border-rose-500/40 text-rose-200 text-[11px] font-semibold px-2 py-1 hover:bg-rose-600/25"
                            >
                              <RotateCcw className="w-3 h-3" /> Refund
                            </button>
                          )}
                        </td>
                      </tr>
                      {isOpen && (
                        <tr data-testid={`payment-detail-${tx.session_id}`}>
                          <td colSpan="7" className="px-4 py-4 bg-slate-950/60 border-t border-slate-800">
                            <div className="grid md:grid-cols-3 gap-4 text-[12px]">
                              <div>
                                <div className="text-[10px] uppercase tracking-wider font-bold text-slate-500 mb-1">
                                  Allocation
                                </div>
                                <div className="text-slate-300">Clinic revenue: <span className="font-mono">{fmtCents(tx.clinic_revenue_cents)}</span></div>
                                <div className="text-slate-300">Nodera fee: <span className="font-mono">{fmtCents(tx.nodera_fee_cents)}</span></div>
                                <div className="text-slate-300">Transfer group: <span className="font-mono text-slate-500 text-[10px]">{tx.transfer_group || "—"}</span></div>
                              </div>
                              <div className="md:col-span-2">
                                <div className="text-[10px] uppercase tracking-wider font-bold text-slate-500 mb-1">
                                  Transfer legs
                                </div>
                                {(tx.transfer_legs || []).length === 0 ? (
                                  <div className="text-slate-500 italic">No direct transfer legs.</div>
                                ) : (
                                  <ul className="space-y-1">
                                    {tx.transfer_legs.map((leg, i) => (
                                      <li key={i} className="flex items-center justify-between gap-2">
                                        <span className="text-slate-300 truncate">
                                          {leg.service_name} · <span className="text-slate-500">{leg.route}</span>
                                        </span>
                                        <span className="font-mono text-slate-200">
                                          {fmtCents(leg.amount_cents)}{" "}
                                          <span className={`text-[10px] uppercase ${
                                            leg.reversal_status === "reversed"
                                              ? "text-rose-300"
                                              : leg.status === "transferred"
                                                ? "text-emerald-300"
                                                : "text-slate-500"
                                          }`}>
                                            {leg.reversal_status || leg.status}
                                          </span>
                                        </span>
                                      </li>
                                    ))}
                                  </ul>
                                )}
                                {tx.refunded && (
                                  <div className="mt-3 text-[11px] text-rose-200">
                                    Refunded {fmtDate(tx.refund_at)} · refund_id <span className="font-mono">{tx.refund_id}</span>
                                  </div>
                                )}
                              </div>
                            </div>
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
      {refundFor && (
        <RefundConfirm
          tx={refundFor}
          onClose={() => setRefundFor(null)}
          onConfirmed={onRefunded}
        />
      )}
    </div>
  );
}
